import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, HkdInfo } from "../api";
import { formatVND, formatDate } from "../utils";
import { exportBangKe01TNDN } from "../utils/exportBangKe01TNDN";
import { GlassPanel, KpiCard, Modal, EmptyState, toast } from "../components";

interface PLEntry {
  id: number;
  purchase_date: string;
  seller_name: string;
  seller_address?: string | null;
  seller_id_card?: string | null;
  seller_phone?: string | null;
  seller_bank_account?: string | null;
  item_description: string;
  quantity: number;
  unit?: string | null;
  unit_price?: number | null;
  total_amount: number;
  payment_method?: string | null;
  category?: string | null;
  note?: string | null;
  attachment_url?: string | null;
  created_by?: string;
  created_at?: string;
  import_order_id?: number | null;
}

type PLForm = {
  purchase_date: string;
  seller_name: string;
  seller_address: string;
  seller_id_card: string;
  seller_phone: string;
  seller_bank_account: string;
  item_description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_amount: number;
  payment_method: string;
  category: string;
  note: string;
};

const emptyForm = (): PLForm => ({
  purchase_date: new Date().toISOString().slice(0, 10),
  seller_name: "",
  seller_address: "",
  seller_id_card: "",
  seller_phone: "",
  seller_bank_account: "",
  item_description: "",
  quantity: 1,
  unit: "",
  unit_price: 0,
  total_amount: 0,
  payment_method: "cash",
  category: "",
  note: "",
});

type TabKey = "all" | "this_month" | "last_month" | "custom";

function getMonthRange(offset: number): { from: string; to: string } {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const from = d.toISOString().slice(0, 10);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const to = last.toISOString().slice(0, 10);
  return { from, to };
}

/** Bảng kê 01/TNDN — mua hàng không hóa đơn (NĐ 126/2020) */
export default function PurchaseLedgerPage() {
  const [entries, setEntries] = useState<PLEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("all");
  const [customFrom, setCustomFrom] = useState(() => getMonthRange(0).from);
  const [customTo, setCustomTo] = useState(() => getMonthRange(0).to);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<PLForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [viewEntry, setViewEntry] = useState<PLEntry | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ seller_name: "", seller_address: "", seller_id_card: "", seller_phone: "", seller_bank_account: "", item_description: "", total_amount: 0, note: "" });
  const [saving, setSaving] = useState(false);
  const [hkd, setHkd] = useState<HkdInfo>({ name: "", tax_code: "", address: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.hkdInfo.get().then(setHkd).catch(() => {});
  }, []);

  const getDateRange = () => {
    if (tab === "all") return { from: "", to: "" };
    if (tab === "this_month") return getMonthRange(0);
    if (tab === "last_month") return getMonthRange(-1);
    return { from: customFrom, to: customTo };
  };

  useEffect(() => {
    loadEntries();
  }, [tab, customFrom, customTo]);

  const loadEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getDateRange();
      const r = await api.purchaseLedger.list({ from, to, limit: 500 });
      setEntries((r as any).entries || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = entries.reduce((s, e) => s + (e.total_amount || 0), 0);
  const uniqueSuppliers = new Set(entries.map((e) => e.seller_name)).size;

  const handleSubmit = async () => {
    if (!form.seller_name.trim() || !form.item_description.trim() || form.total_amount <= 0) {
      toast.error("Nhập tên người bán, mô tả hàng và số tiền > 0");
      return;
    }
    setSubmitting(true);
    try {
      await api.purchaseLedger.create({
        purchase_date: form.purchase_date,
        seller_name: form.seller_name,
        seller_address: form.seller_address || undefined,
        seller_id_card: form.seller_id_card || undefined,
        seller_phone: form.seller_phone || undefined,
        seller_bank_account: form.seller_bank_account || undefined,
        item_description: form.item_description,
        quantity: form.quantity,
        unit: form.unit || undefined,
        unit_price: form.unit_price || undefined,
        total_amount: form.total_amount,
        payment_method: form.payment_method || undefined,
        category: form.category || undefined,
        note: form.note || undefined,
      } as any);
      toast.success("Đã lưu bảng kê");
      setShowModal(false);
      setForm(emptyForm());
      await loadEntries();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi lưu bảng kê");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Tab navigation + daterange */}
      <div className="daterange">
        <div className="tabs">
          <button className={tab === "all" ? "tab-item active" : "tab-item"} onClick={() => setTab("all")}>Tất cả</button>
          <button className={tab === "this_month" ? "tab-item active" : "tab-item"} onClick={() => setTab("this_month")}>Tháng này</button>
          <button className={tab === "last_month" ? "tab-item active" : "tab-item"} onClick={() => setTab("last_month")}>Tháng trước</button>
          <button className={tab === "custom" ? "tab-item active" : "tab-item"} onClick={() => setTab("custom")}>Tuỳ chọn</button>
        </div>
        {tab === "custom" && (
          <>
            <div className="field">
              <label>Từ</label>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            </div>
            <div className="field">
              <label>Đến</label>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          </>
        )}
      </div>

      {/* KPI cards */}
      <div className="kpi-grid">
        <KpiCard label="Số bảng kê" value={entries.length.toString()} dotColor="blue" note="trong kỳ" />
        <KpiCard label="Tổng tiền" value={formatVND(totalAmount)} dotColor="bad" note="mua không HĐ" />
        <KpiCard label="Số NCC" value={uniqueSuppliers.toString()} dotColor="warn" note="người bán khác nhau" />
      </div>

      {/* Data table */}
      <GlassPanel title="Bảng kê mua hàng" badge={`${entries.length} dòng`}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button className="btn" onClick={() => { setForm(emptyForm()); setShowModal(true); }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Thêm bảng kê
          </button>
          <button
            className="btn"
            disabled={!entries.length}
            onClick={() => {
              const { from, to } = getDateRange();
              exportBangKe01TNDN({ hkd, entries, from: from || undefined, to: to || undefined });
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
            Xuất Bảng kê 01/TNDN (Excel)
          </button>
        </div>

        {loading ? (
          <div className="loading">Đang tải…</div>
        ) : error ? (
          <div className="error">Lỗi: {error}</div>
        ) : entries.length === 0 ? (
          <EmptyState icon="receipt_long" title="Chưa có bảng kê nào" description="Thêm để theo dõi mua hàng không hóa đơn (NĐ 126/2020)." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th>Người bán</th>
                  <th>Mô tả</th>
                  <th style={{ textAlign: "right" }}>Số tiền</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.purchase_date)}</td>
                    <td>{e.seller_name}</td>
                    <td>{e.item_description || "—"}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{formatVND(e.total_amount)}</td>
                    <td>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 11, padding: "2px 6px" }}
                        onClick={() => setViewEntry(e)}
                        title="Xem chi tiết"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>visibility</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>

      {/* Modal thêm bảng kê */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Thêm bảng kê"
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Huỷ</button>
            <button className="btn" onClick={handleSubmit} disabled={submitting}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
              {submitting ? "Đang lưu…" : "Lưu"}
            </button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Section: Thông tin chung */}
          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", padding: "0 6px" }}>Thông tin chung</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ngày mua *</label>
              <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Hình thức thanh toán</label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                <option value="cash">Tiền mặt</option>
                <option value="transfer">Chuyển khoản</option>
                <option value="other">Khác</option>
              </select>

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ghi chú</label>
              <input placeholder="Ghi chú thêm (nếu có)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </fieldset>

          {/* Section: Người bán */}
          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", padding: "0 6px" }}>Người bán</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tên người bán *</label>
              <input placeholder="Nguyễn Văn A" value={form.seller_name} onChange={(e) => setForm({ ...form, seller_name: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số CMND/CCCD</label>
              <input placeholder="012345678901" value={form.seller_id_card} onChange={(e) => setForm({ ...form, seller_id_card: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Địa chỉ</label>
              <input placeholder="Địa chỉ người bán" value={form.seller_address} onChange={(e) => setForm({ ...form, seller_address: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số điện thoại</label>
              <input placeholder="0901234567" value={form.seller_phone} onChange={(e) => setForm({ ...form, seller_phone: e.target.value })} />

              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số tài khoản ngân hàng</label>
              <input placeholder="STK ngân hàng (nếu có)" value={form.seller_bank_account} onChange={(e) => setForm({ ...form, seller_bank_account: e.target.value })} />
            </div>
          </fieldset>

          {/* Section: Hàng hóa */}
          <fieldset style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
            <legend style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", padding: "0 6px" }}>Hàng hóa</legend>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Mô tả hàng hoá *</label>
              <input placeholder="Tên hàng hoá / dịch vụ" value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} />

              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số lượng</label>
                  <input type="number" inputMode="numeric" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Đơn vị</label>
                  <input placeholder="kg, cái, ..." value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Đơn giá</label>
                  <input type="number" inputMode="numeric" placeholder="0" value={form.unit_price || ""} onChange={(e) => {
                    const up = Number(e.target.value);
                    setForm({ ...form, unit_price: up, total_amount: up * form.quantity });
                  }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tổng tiền *</label>
                  <input type="number" inputMode="numeric" placeholder="0" value={form.total_amount || ""} onChange={(e) => setForm({ ...form, total_amount: Number(e.target.value) })} />
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </Modal>

      {/* Modal xem chi tiết */}
      <Modal
        open={!!viewEntry}
        onClose={() => { setViewEntry(null); setEditMode(false); }}
        title="Chi tiết bảng kê"
        width={560}
        footer={
          <button className="btn btn-ghost" onClick={() => { setViewEntry(null); setEditMode(false); }}>Đóng</button>
        }
      >
        {viewEntry && !editMode && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
            <div><strong>Ngày mua:</strong> {formatDate(viewEntry.purchase_date)}</div>
            <div><strong>Người bán:</strong> {viewEntry.seller_name}</div>
            {viewEntry.seller_id_card && <div><strong>Số CMND/CCCD:</strong> {viewEntry.seller_id_card}</div>}
            {viewEntry.seller_address && <div><strong>Địa chỉ:</strong> {viewEntry.seller_address}</div>}
            {viewEntry.seller_phone && <div><strong>SĐT:</strong> {viewEntry.seller_phone}</div>}
            {viewEntry.seller_bank_account && <div><strong>STK ngân hàng:</strong> {viewEntry.seller_bank_account}</div>}
            <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "4px 0" }} />
            <div><strong>Mô tả:</strong> {viewEntry.item_description}</div>
            <div><strong>Số lượng:</strong> {viewEntry.quantity} {viewEntry.unit || ""}</div>
            {viewEntry.unit_price != null && <div><strong>Đơn giá:</strong> {formatVND(viewEntry.unit_price)}</div>}
            <div><strong>Tổng tiền:</strong> <span style={{ fontWeight: 700, color: "var(--accent-red)" }}>{formatVND(viewEntry.total_amount)}</span></div>
            {viewEntry.payment_method && <div><strong>Thanh toán:</strong> {viewEntry.payment_method === "cash" ? "Tiền mặt" : viewEntry.payment_method === "transfer" ? "Chuyển khoản" : viewEntry.payment_method}</div>}
            {viewEntry.note && <div><strong>Ghi chú:</strong> {viewEntry.note}</div>}
            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => {
                setEditForm({
                  seller_name: viewEntry.seller_name || "",
                  seller_address: viewEntry.seller_address || "",
                  seller_id_card: viewEntry.seller_id_card || "",
                  seller_phone: viewEntry.seller_phone || "",
                  seller_bank_account: viewEntry.seller_bank_account || "",
                  item_description: viewEntry.item_description || "",
                  total_amount: viewEntry.total_amount || 0,
                  note: viewEntry.note || "",
                });
                setEditMode(true);
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                Sửa thông tin người bán
              </button>
              {viewEntry.import_order_id && (
                <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => navigate(`/warehouse/import?order=${viewEntry.import_order_id}`)}>
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>link</span>
                  Mở đơn nhập gốc
                </button>
              )}
            </div>
          </div>
        )}
        {viewEntry && editMode && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tên người bán</label>
            <input value={editForm.seller_name} onChange={(e) => setEditForm({ ...editForm, seller_name: e.target.value })} />
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Địa chỉ</label>
            <input value={editForm.seller_address} onChange={(e) => setEditForm({ ...editForm, seller_address: e.target.value })} />
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số CMND/CCCD</label>
            <input value={editForm.seller_id_card} onChange={(e) => setEditForm({ ...editForm, seller_id_card: e.target.value })} />
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>SĐT</label>
            <input value={editForm.seller_phone} onChange={(e) => setEditForm({ ...editForm, seller_phone: e.target.value })} />
            <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>STK ngân hàng</label>
            <input value={editForm.seller_bank_account} onChange={(e) => setEditForm({ ...editForm, seller_bank_account: e.target.value })} />
            {!viewEntry.import_order_id && (
              <>
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Mô tả</label>
                <input value={editForm.item_description} onChange={(e) => setEditForm({ ...editForm, item_description: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tổng tiền</label>
                <input type="number" value={editForm.total_amount} onChange={(e) => setEditForm({ ...editForm, total_amount: Number(e.target.value) })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ghi chú</label>
                <input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
              </>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="btn" disabled={saving} onClick={async () => {
                setSaving(true);
                try {
                  if (viewEntry.import_order_id) {
                    await api.importOrders.update(viewEntry.import_order_id, {
                      supplier_name: editForm.seller_name || null,
                      supplier_address: editForm.seller_address || null,
                      supplier_tax_id: editForm.seller_id_card || null,
                      supplier_phone: editForm.seller_phone || null,
                      supplier_bank_account: editForm.seller_bank_account || null,
                    } as any);
                  } else {
                    await api.purchaseLedger.update(viewEntry.id, {
                      seller_name: editForm.seller_name || null,
                      seller_address: editForm.seller_address || null,
                      seller_id_card: editForm.seller_id_card || null,
                      seller_phone: editForm.seller_phone || null,
                      seller_bank_account: editForm.seller_bank_account || null,
                      item_description: editForm.item_description || null,
                      total_amount: editForm.total_amount,
                      note: editForm.note || null,
                    });
                  }
                  toast.success("Đã lưu");
                  setEditMode(false);
                  setViewEntry(null);
                  loadEntries();
                } catch (e: any) {
                  toast.error(e.message || "Lỗi cập nhật");
                } finally { setSaving(false); }
              }}>
                {saving ? "Đang lưu..." : "Lưu"}
              </button>
              <button className="btn btn-ghost" onClick={() => setEditMode(false)}>Huỷ</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
